import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TeacherAssignmentService from '../../services/teacherAssignment.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const emptyForm = {
  user_id: '',
  cycle_id: '',
  grade_id: '',
  curso_id: '',
};

const TeacherAssignments = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [grades, setGrades] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [filterUserId, setFilterUserId] = useState(searchParams.get('user_id') || '');
  const [form, setForm] = useState({ ...emptyForm, user_id: searchParams.get('user_id') || '' });
  const [loading, setLoading] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const canManage = hasPermission('attendances.manage');

  const loadAssignments = useCallback(async () => {
    const params = {};
    if (filterUserId) params.user_id = filterUserId;
    const res = await TeacherAssignmentService.getAll(params);
    setAssignments(res.data?.data || []);
  }, [filterUserId]);

  const loadMeta = useCallback(async () => {
    const res = await TeacherAssignmentService.getFormData();
    const payload = res.data?.data || {};
    setUsers(payload.users || []);
    setGrades(payload.grades || []);
    setCursos(payload.cursos || []);
    setCycles(payload.cycles || []);
  }, []);

  useEffect(() => {
    if (!canManage) return;
    (async () => {
      try {
        setLoadingAssignments(true);
        await loadAssignments();
      } catch {
        setError(t('teacherAssignments.errorLoading'));
      } finally {
        setLoadingAssignments(false);
      }
    })();
  }, [canManage, filterUserId, loadAssignments, t]);

  useEffect(() => {
    if (!canManage) return;
    (async () => {
      try {
        setLoading(true);
        setError('');
        await loadMeta();
      } catch (err) {
        setError(err.response?.data?.message || t('teacherAssignments.errorLoading'));
      } finally {
        setLoading(false);
      }
    })();
  }, [canManage, loadMeta, t]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.user_id || (!form.grade_id && !form.curso_id)) {
      setSnackbar({ open: true, message: t('teacherAssignments.needGradeOrCourse'), severity: 'warning' });
      return;
    }
    try {
      setSaving(true);
      await TeacherAssignmentService.create({
        user_id: Number(form.user_id),
        cycle_id: form.cycle_id ? Number(form.cycle_id) : null,
        grade_id: form.grade_id ? Number(form.grade_id) : null,
        curso_id: form.curso_id ? Number(form.curso_id) : null,
      });
      setForm({ ...emptyForm, user_id: filterUserId || '' });
      await loadAssignments();
      setSnackbar({ open: true, message: t('teacherAssignments.saved'), severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || t('teacherAssignments.saveError'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await TeacherAssignmentService.remove(id);
      await loadAssignments();
      setSnackbar({ open: true, message: t('teacherAssignments.removed'), severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || t('teacherAssignments.removeError'),
        severity: 'error',
      });
    }
  };

  if (!canManage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('teacherAssignments.noPermission')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/attendance')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t('teacherAssignments.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('teacherAssignments.subtitle')}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('teacherAssignments.teacher')}</InputLabel>
              <Select
                label={t('teacherAssignments.teacher')}
                value={form.user_id}
                onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                required
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={String(user.id)}>
                    {user.name} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('attendance.cycle')}</InputLabel>
              <Select
                label={t('attendance.cycle')}
                value={form.cycle_id}
                onChange={(e) => setForm({ ...form, cycle_id: e.target.value })}
              >
                <MenuItem value="">{t('teacherAssignments.anyCycle')}</MenuItem>
                {cycles.map((cycle) => (
                  <MenuItem key={cycle.id} value={String(cycle.id)}>
                    {cycle.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('attendance.grade')}</InputLabel>
              <Select
                label={t('attendance.grade')}
                value={form.grade_id}
                onChange={(e) => setForm({ ...form, grade_id: e.target.value })}
              >
                <MenuItem value="">{t('teacherAssignments.none')}</MenuItem>
                {grades.map((grade) => (
                  <MenuItem key={grade.id} value={String(grade.id)}>
                    {grade.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('attendance.course')}</InputLabel>
              <Select
                label={t('attendance.course')}
                value={form.curso_id}
                onChange={(e) => setForm({ ...form, curso_id: e.target.value })}
              >
                <MenuItem value="">{t('teacherAssignments.none')}</MenuItem>
                {cursos.map((curso) => (
                  <MenuItem key={curso.id} value={String(curso.id)}>
                    {curso.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={saving}
            >
              {t('teacherAssignments.add')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <FormControl size="small" sx={{ minWidth: 240, mb: 2 }}>
        <InputLabel>{t('teacherAssignments.filterByTeacher')}</InputLabel>
        <Select
          label={t('teacherAssignments.filterByTeacher')}
          value={filterUserId}
          onChange={(e) => setFilterUserId(e.target.value)}
        >
          <MenuItem value="">{t('teacherAssignments.allTeachers')}</MenuItem>
          {users.map((user) => (
            <MenuItem key={user.id} value={String(user.id)}>
              {user.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading || loadingAssignments ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>{t('teacherAssignments.teacher')}</TableCell>
                <TableCell>{t('attendance.cycle')}</TableCell>
                <TableCell>{t('attendance.grade')}</TableCell>
                <TableCell>{t('attendance.course')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {t('teacherAssignments.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                assignments.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.user?.name || row.user_id}</TableCell>
                    <TableCell>{row.cycle?.name || t('teacherAssignments.anyCycle')}</TableCell>
                    <TableCell>{row.grade?.name || '—'}</TableCell>
                    <TableCell>{row.curso?.name || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeacherAssignments;
